import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  OfferStatus,
  InspectionStatus,
  InspectionType,
  ListingType,
  AppraiseStatus,
  FinancingStatus,
  TitleStatus,
  ClosingStatus,
} from '@prisma/client';
import { listingsWithUploadsAndOpenHouses } from './listings';

interface DemoState {
  isDemoMode: boolean;
  currentStep: number;
  totalSteps: number;
  demoType: 'buying' | 'selling' | null;
  mockListings: listingsWithUploadsAndOpenHouses[];
  mockShowings: any[];
  mockOffers: any[];
  mockInspections: any[];
  mockTransactions: any[];
  mockStatuses: Record<string, any>;
}

const initialState: DemoState = {
  isDemoMode: false,
  currentStep: 0,
  totalSteps: 6,
  demoType: null,
  mockListings: [],
  mockShowings: [],
  mockOffers: [],
  mockInspections: [],
  mockTransactions: [],
  mockStatuses: {},
};

const demoSlice = createSlice({
  name: 'demo',
  initialState,
  reducers: {
    startDemo: (
      state,
      action: PayloadAction<{ type: 'buying' | 'selling' } | undefined>
    ) => {
      state.isDemoMode = true;
      state.currentStep = 0;
      state.demoType = action.payload?.type || 'buying';

      if (action.payload?.type === 'selling') {
        // Set total steps for selling tour
        state.totalSteps = 4;

        // Generate mock seller listing
        const mockSellerListing: listingsWithUploadsAndOpenHouses = {
          id: 'demo-seller-listing-1',
          type: ListingType.SELLER,
          address: '456 Maple Street',
          area: 2200,
          city: 'Birmingham',
          state: 'AL',
          zip: '35209',
          county: 'Jefferson',
          lat: 33.5186,
          lng: -86.8104,
          description: 'Charming 3-bedroom home in established neighborhood',
          beds: 3,
          baths: 2,
          sqft: 2200,
          lotSize: 6500,
          yearBuilt: 2015,
          listingAgent: 'Sarah Johnson',
          listingAgentCompany: 'Homepoint Realty',
          listingAgentPhone: '(205) 555-0199',
          listingAgentEmail: 'sarah@homepoint.co',
          listingDate: new Date().toISOString(),
          closingDate: null,
          closingType: null,
          images: [],
          price: 385000,
          marketStatus: 'active',
          sellerId: 'demo-user',
          buyerId: null,
          priceTrend: null,
          propertyType: 'Single Family',
          refId: null,
          slug: 'demo-seller-listing',
          status: 'APPROVED',
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          userId: 'demo-user',
          uuid: 'demo-seller-uuid-1',
          underContract: false,
          parkingInfo: '2 car garage',
          liveDate: new Date().toISOString(),
          pros: [],
          cons: [],
          feedback: null,
          overallFeedback: null,
          uploads: [],
          openHouses: [],
          user: null,
          accessInfo: 'Lockbox on front door, code: 1234',
          mlsListing: {
            activationDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
          },
        } as any;

        state.mockListings = [mockSellerListing];

        // Generate mock showings for seller with user objects
        const mockBuyer1 = {
          id: 'demo-buyer-1',
          firstName: 'Michael',
          lastName: 'Thompson',
          email: 'michael.t@email.com',
          phone: '(205) 555-3333',
        } as any;

        const mockBuyer2 = {
          id: 'demo-buyer-2',
          firstName: 'Jennifer',
          lastName: 'Davis',
          email: 'jen.davis@email.com',
          phone: '(205) 555-5555',
        } as any;

        const mockSellerShowings = [
          {
            id: 'demo-seller-showing-1',
            type: 'SELLER' as any,
            userId: 'demo-buyer-1',
            user: mockBuyer1,
            listings: mockSellerListing,
            listingId: 'demo-seller-listing-1',
            proposedTime: new Date(
              Date.now() + 2 * 24 * 60 * 60 * 1000
            ).toISOString(), // 2 days from now
            status: 'SCHEDULED',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            duration: 30,
            feedback: null,
            accepted: true,
            isAgent: false,
            confirmationCode: 'ABC123',
            showingNotificationPhone: '(205) 555-1111',
          },
          {
            id: 'demo-seller-showing-2',
            type: 'SELLER' as any,
            userId: 'demo-buyer-2',
            user: mockBuyer2,
            listings: mockSellerListing,
            listingId: 'demo-seller-listing-1',
            proposedTime: new Date(
              Date.now() + 3 * 24 * 60 * 60 * 1000
            ).toISOString(), // 3 days from now
            status: 'SCHEDULED',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            duration: 45,
            feedback: null,
            accepted: true,
            isAgent: true,
            confirmationCode: 'XYZ789',
            showingNotificationPhone: '(205) 555-2222',
          },
        ];

        state.mockShowings = mockSellerShowings;

        // Generate mock offers for seller
        const mockSellerOffers = [
          {
            id: 'demo-seller-offer-1',
            listingId: 'demo-seller-listing-1',
            listings: mockSellerListing, // Add the listing object
            user: mockBuyer1, // Add user object
            price: 375000,
            status: OfferStatus.ACCEPTED, // Changed to ACCEPTED for transaction
            userId: 'demo-buyer-1',
            createdAt: new Date(
              Date.now() - 1 * 24 * 60 * 60 * 1000
            ).toISOString(), // 1 day ago
            updatedAt: new Date().toISOString(),
            termiteBond: true,
            buyerFirstName: 'Michael',
            buyerLastName: 'Thompson',
            buyerPhone: '(205) 555-3333',
            buyerEmail: 'michael.t@email.com',
            closingDate: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000
            ).toISOString(), // 30 days from now
            lenderName: 'First National Bank',
            lenderPhone: '(205) 555-4444',
            lenderEmail: 'loans@firstnational.com',
            inspectionPeriodDays: 10,
            downPayment: 75000,
            underwritingDays: 21,
            notes:
              'Very interested in this property. Flexible on closing date.',
            homeWarrantyType: 'SELLER_PAID',
            sellerConcessionsCents: 500000, // $5,000
          },
          {
            id: 'demo-seller-offer-2',
            listingId: 'demo-seller-listing-1',
            listings: mockSellerListing, // Add the listing object
            user: mockBuyer2, // Add user object
            price: 380000,
            status: OfferStatus.PENDING,
            userId: 'demo-buyer-2',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            termiteBond: false,
            buyerFirstName: 'Jennifer',
            buyerLastName: 'Davis',
            buyerPhone: '(205) 555-5555',
            buyerEmail: 'jen.davis@email.com',
            closingDate: new Date(
              Date.now() + 45 * 24 * 60 * 60 * 1000
            ).toISOString(), // 45 days from now
            lenderName: 'Regions Bank',
            lenderPhone: '(205) 555-6666',
            lenderEmail: 'mortgage@regions.com',
            inspectionPeriodDays: 14,
            downPayment: 100000,
            underwritingDays: 30,
            notes: 'Cash offer, can close quickly if needed.',
            homeWarrantyType: 'NONE',
            sellerConcessionsCents: 0,
          },
        ];

        state.mockOffers = mockSellerOffers;
        state.mockInspections = [];

        // Generate mock transactions for seller (one for each offer that's been accepted)
        const mockSellerTransactions = [
          {
            id: 'demo-seller-transaction-1',
            listingId: 'demo-seller-listing-1',
            listings: mockSellerListing,
            userId: 'demo-buyer-1',
            user: mockBuyer1,
            offerId: 'demo-seller-offer-1',
            offer: mockSellerOffers[0],
            appraisal: AppraiseStatus.SCHEDULED,
            financing: FinancingStatus.PRE_APPROVED,
            title: TitleStatus.ORDERED,
            closing: ClosingStatus.SCHEDULED,
            inspection: InspectionStatus.SCHEDULED,
            closingTime: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000
            ).toISOString(), // 30 days from now
            closingName: 'Homepoint Title Company',
            closingEmail: 'closing@homepoint.co',
            closingPhone: '(205) 555-7777',
            lenderEmail: 'loans@firstnational.com',
            lenderPhone: '(205) 555-4444',
            otherPartyEmail: 'michael.t@email.com',
            otherPartyPhone: '(205) 555-3333',
            appraiserEmail: 'appraisal@propertypros.com',
            appraiserPhone: '(205) 555-8888',
            inspections: [],
            uploads: [],
            addendum: null,
          },
        ];

        state.mockTransactions = mockSellerTransactions;

        // Generate mock status for the seller listing - showing realistic transaction progress
        const mockSellerStatus = {
          showing: {
            data: mockSellerShowings,
            completed: true, // Showings have happened
          },
          offer: {
            data: mockSellerOffers.filter(
              (o) => o.status === OfferStatus.ACCEPTED
            ),
            completed: true, // Offer accepted
          },
          inspection: {
            data: [],
            needed: true,
            pending: false,
            completed: true, // Inspection completed
            occurred: true,
            notApplicable: false,
            inspectionDate: new Date(
              Date.now() - 3 * 24 * 60 * 60 * 1000
            ).toISOString(), // 3 days ago
            addendum: null,
          },
          appraisal: {
            data: mockSellerTransactions[0],
            needed: true,
            pending: false,
            completed: true, // Appraisal completed
            notApplicable: false,
          },
          title: {
            data: mockSellerTransactions[0],
            needed: true,
            completed: false,
            pending: true, // Title work in progress
          },
          financing: {
            data: mockSellerTransactions[0],
            needed: true,
            completed: false,
            pending: true, // Financing in progress
            notApplicable: false,
          },
          closing: {
            data: null,
            pending: false,
            needed: true,
            completed: false, // Not yet scheduled
          },
          finalWalkthrough: {
            data: null,
            pending: false,
            needed: true,
            completed: false, // Not yet scheduled
          },
        };

        state.mockStatuses = {
          'demo-seller-listing-1': mockSellerStatus,
        };
      } else {
        // Original buyer demo data
        state.totalSteps = 5;

        // Generate mock listing
        const mockListing: listingsWithUploadsAndOpenHouses = {
          id: 'demo-listing-1',
          type: ListingType.BUYER,
          address: '123 Dream House Lane',
          area: 2800,
          city: 'Atlanta',
          state: 'GA',
          zip: '30309',
          county: 'Fulton',
          lat: 33.749,
          lng: -84.388,
          description: 'Beautiful 4-bedroom home in a great location',
          beds: 4,
          baths: 3,
          sqft: 2800,
          lotSize: 8000,
          yearBuilt: 2020,
          listingAgent: 'Jane Smith',
          listingAgentCompany: 'Homepoint Realty',
          listingAgentPhone: '(404) 555-0123',
          listingAgentEmail: 'jane@homepoint.co',
          listingDate: new Date().toISOString(),
          closingDate: null,
          closingType: null,
          images: [],
          price: 450000,
          marketStatus: 'active',
          sellerId: null,
          buyerId: 'demo-user',
          priceTrend: null,
          propertyType: 'Single Family',
          refId: null,
          slug: 'demo-listing',
          status: 'APPROVED',
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          userId: 'demo-user',
          uuid: 'demo-uuid-1',
          underContract: false,
          parkingInfo: null,
          liveDate: new Date().toISOString(),
          pros: ['Great location', 'Newly renovated kitchen', 'Large backyard'],
          cons: ['Busy street', 'Needs roof repair'],
          feedback: 'Overall a great property with lots of potential',
          overallFeedback: 'LIKE',
          uploads: [],
          openHouses: [],
          user: null,
          accessInfo: null,
        } as any;

        state.mockListings = [mockListing];

        // No showing scheduled yet - buyer just added the property
        state.mockShowings = [];

        // No offer made yet - buyer is still in the exploration phase
        state.mockOffers = [];

        // No inspections scheduled yet - those come after an offer is accepted
        state.mockInspections = [];

        // No transactions yet - buyer hasn't made an accepted offer
        state.mockTransactions = [];

        // No status yet for buyer demo
        state.mockStatuses = {};
      }

      /* COMMENTED OUT FOR FUTURE USE IF NEEDED
      // Generate mock showing (scheduled)
      const showingDate = new Date();
      showingDate.setDate(showingDate.getDate() + 3); // 3 days from now
      showingDate.setHours(14, 0, 0, 0); // 2:00 PM

      const mockShowing = {
        id: 'demo-showing-1',
        type: 'BUYER' as any,
        userId: 'demo-user',
        listingId: 'demo-listing-1',
        proposedTime: showingDate,
        status: 'SCHEDULED',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        duration: 30,
        feedback: null,
        accepted: true,
        isAgent: false,
        confirmationCode: null,
        showingNotificationPhone: null,
        showingEmailMessageId: null,
        showingTimeMessageId: null,
        showingTimeMessageSid: null,
      };

      state.mockShowings = [mockShowing];

      // Generate mock offer (in review state for demo)
      const mockOffer = {
        id: 'demo-offer-1',
        listingId: 'demo-listing-1',
        price: 445000,
        status: OfferStatus.REVIEWED,
        userId: 'demo-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        termiteBond: false,
        buyerFirstName: 'John',
        buyerLastName: 'Doe',
        buyerPhone: '(404) 555-1234',
        buyerEmail: 'john.doe@email.com',
        closingDate: null,
        lenderName: null,
        lenderPhone: null,
        lenderEmail: null,
        inspectionPeriodDays: 10,
        downPayment: 89000,
        underwritingDays: 30,
        notes: null,
        contractSignedUrl: null,
        inspectionAdditionalTerms: null,
        financingAdditionalTerms: null,
        otherAdditionalTerms: null,
        closingCostsTerms: null,
        earnestMoneyTerms: null,
        sellerConcessionsCents: 0,
        homeWarrantyType: 'NONE',
      };

      state.mockOffers = [mockOffer];

      // Generate mock inspection
      const mockInspection = {
        id: 'demo-inspection-1',
        type: InspectionType.HOME,
        listingId: 'demo-listing-1',
        status: InspectionStatus.SCHEDULED,
        vendorName: 'Pro Home Inspections',
        vendorPhone: '(404) 555-9876',
        inspectionDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 days from now
        cost: 450,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: 'demo-user',
        reportUrl: null,
        transactionId: null,
        buyerPhone: null,
        orderId: null,
        inspectionId: null,
        vendorResponseUrl: null,
        vendorEmail: null,
      };

      state.mockInspections = [mockInspection];
      */
    },

    endDemo: (state) => {
      state.isDemoMode = false;
      state.currentStep = 0;
      state.demoType = null;
      state.mockListings = [];
      state.mockShowings = [];
      state.mockOffers = [];
      state.mockInspections = [];
      state.mockTransactions = [];
      state.mockStatuses = {};
    },

    nextStep: (state) => {
      if (state.currentStep < state.totalSteps - 1) {
        state.currentStep += 1;
      }
    },

    previousStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },

    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
  },
});

export const { startDemo, endDemo, nextStep, previousStep, setStep } =
  demoSlice.actions;
export default demoSlice.reducer;
